/**
 * Fetches a PDF (or other file) from the API with the API key header
 * and either opens it in a new browser tab (default) or downloads it
 * with a friendly filename.
 *
 * This replaces window.open(url) calls which skip custom headers.
 * The global fetch wrapper in config.js automatically attaches X-API-Key.
 *
 * Filename resolution: the API sets Content-Disposition with a friendly
 * filename (e.g. `Quote #310B.pdf`), which the API exposes cross-origin
 * via Access-Control-Expose-Headers. An explicit `filename` argument
 * overrides what the server sent.
 *
 * Modes:
 * - Default (tab): opens the blob in a new tab for viewing/printing.
 *   Note the browser names any save from that tab after the blob URL's
 *   UUID — there is no way to name a blob-viewer tab's download.
 * - `{ download: true }`: downloads directly via a synthetic anchor with
 *   `download={filename}`, so the saved file gets the friendly name.
 *   Falls back to the tab mode if no filename could be resolved (e.g.
 *   the server didn't send one), so the click always does something.
 */
import { apiErrorMessage } from './apiError';

export default async function openPdf(url, filename, opts = {}) {
  const { download = false } = opts;
  try {
    const res = await fetch(url);
    if (!res.ok) {
      throw new Error(await apiErrorMessage(res));
    }

    if (!filename) {
      const cd = res.headers.get("Content-Disposition") || "";
      const match =
        cd.match(/filename\*=UTF-8''([^;]+)/i) ||
        cd.match(/filename="([^"]+)"/i) ||
        cd.match(/filename=([^;]+)/i);
      if (match) {
        try {
          filename = decodeURIComponent(match[1].trim());
        } catch {
          filename = match[1].trim();
        }
      }
    }

    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);

    const a = document.createElement("a");
    a.href = blobUrl;
    if (download && filename) {
      a.download = filename;
    } else {
      a.target = "_blank";
      a.rel = "noopener noreferrer";
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);

    // Revoke after a short delay so the new tab has time to load.
    setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
  } catch (err) {
    console.error("Failed to open PDF:", err);
    alert("Failed to open PDF. Please try again.");
  }
}
